import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, DollarSign, Shield, FileText, Calculator, ChevronDown, Lock,
  Download, Info, AlertCircle, CheckCircle2, Sparkles, Mail, Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const money = (n) => `$${fmt(n)}`;
const pct = (p) => `${(Number(p || 0) * 100).toFixed(p < 0.01 ? 2 : 1)}%`;

const DEFAULT_INPUTS = {
  priceBasis: "home",         // "home" | "qualified"
  price: 350_000,
  down_pct: 0.10,
  is_bahamian: true,
  first_time: false,
  property_type: "single_family",
  age_band: "under_40",
  coastal: false,
};

/**
 * ClosingCostEstimator — the single shared UI used by both the Pre-Qual
 * results page and the Buying-Power Pro report. The math is server-side so
 * the two surfaces always match.
 *
 * Props:
 *   mode          "free" | "premium"       (premium path needs `email`)
 *   locked        bool                     true to render blurred-locked card
 *   email         string                   entitlement key for premium
 *   qualifiedLoan number                   enables "What I qualify for" basis
 *   bankMatches   [{bank_id, bank_name, closing_fee_pct?, home_insurance_pct?, life_insurance_pct?}]
 *   initialInputs partial of DEFAULT_INPUTS
 *   onUnlockClick fn                       locked-card CTA handler
 */
export default function ClosingCostEstimator({
  mode = "free",
  locked = false,
  email,
  qualifiedLoan,
  bankMatches,
  initialInputs,
  onUnlockClick,
  title = "Closing Cost & Out-of-Pocket Estimator",
}) {
  const [inputs, setInputs] = useState({ ...DEFAULT_INPUTS, ...initialInputs });
  const [result, setResult] = useState(null);
  const [perBank, setPerBank] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("lowest_cash");  // lowest_cash | bank_name

  const setI = (patch) => setInputs((p) => ({ ...p, ...patch }));

  // When user flips to "What I qualify for" basis, recompute price from
  // max loan ÷ (1 - down_pct).
  useEffect(() => {
    if (inputs.priceBasis === "qualified" && qualifiedLoan) {
      const dp = Math.max(0, Math.min(0.999, Number(inputs.down_pct) || 0));
      const price = Math.round(Number(qualifiedLoan) / (1 - dp));
      if (!isNaN(price) && price !== inputs.price) {
        setI({ price });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs.priceBasis, inputs.down_pct, qualifiedLoan]);

  const payload = useMemo(() => ({
    price: Number(inputs.price) || 0,
    down_pct: Number(inputs.down_pct) || 0,
    is_bahamian: !!inputs.is_bahamian,
    first_time: !!(inputs.is_bahamian && inputs.first_time),
    property_type: inputs.property_type,
    age_band: inputs.age_band,
    coastal: !!inputs.coastal,
  }), [inputs]);

  const compute = useCallback(async () => {
    if (locked) return;
    setLoading(true);
    setError("");
    try {
      if (mode === "premium") {
        const res = await axios.post(`${API}/api/estimator/premium`, { ...payload, email });
        setResult(res.data.base);
        setPerBank([]);
      } else {
        const body = { ...payload };
        if (bankMatches?.length) body.bank_overrides = bankMatches;
        const res = await axios.post(`${API}/api/estimator/calculate`, body);
        setResult(res.data.base);
        setPerBank(res.data.per_bank || []);
      }
    } catch (e) {
      if (e?.response?.status === 402) {
        setError("Premium unlock required to view your total out-of-pocket cost.");
      } else {
        setError(e?.response?.data?.detail || "Could not calculate your estimate. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [payload, mode, email, bankMatches, locked]);

  // Auto-compute on mount + whenever inputs change
  useEffect(() => {
    if (locked) return;
    const t = setTimeout(compute, 180);
    return () => clearTimeout(t);
  }, [compute, locked]);

  // Sort hint for the per-bank table — must stay above the early-return guard
  // so React's rules-of-hooks hold whether the card is locked or not.
  const banksSorted = useMemo(() => {
    const list = [...perBank];
    if (sortBy === "lowest_cash") {
      list.sort((a, b) => a.total_out_of_pocket - b.total_out_of_pocket);
    } else {
      list.sort((a, b) => (a.bank_name || "").localeCompare(b.bank_name || ""));
    }
    return list;
  }, [perBank, sortBy]);

  // --- Locked (free Buying-Power tier) ---------------------------------
  if (locked) {
    return (
      <div
        className="relative rounded-2xl bg-gradient-to-br from-[#0F1724] via-[#152034] to-[#0C1120] border border-[#1F2A44] p-6 overflow-hidden"
        data-testid="closing-cost-estimator-locked"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none select-none blur-sm">
          <MockBreakdown />
        </div>
        <div className="relative z-10 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 border border-amber-400/30 px-3 py-1 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
              Pro only
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-white font-cabinet mb-1">
            See your total out-of-pocket cost
          </h3>
          <p className="text-sm text-[#9BA8C4] max-w-md mx-auto mb-5">
            Down payment, government taxes, legal fees, insurance — every line itemized,
            Bahamian first-time exemption automatically applied, Pro-only.
          </p>
          <Button
            onClick={onUnlockClick}
            className="btn-aqua h-12 px-6 inline-flex items-center justify-center gap-2"
            data-testid="estimator-unlock-btn"
          >
            <Lock className="w-4 h-4" />
            Unlock for $29.99
          </Button>
        </div>
      </div>
    );
  }

  const sections = result?.sections;
  const canUseQualified = Number(qualifiedLoan) > 0;

  return (
    <div
      className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden"
      data-testid="closing-cost-estimator"
    >
      {/* Header */}
      <div className="p-5 sm:p-6 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-[#52BFB8]/10 text-[#3E9690] px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider mb-2">
              <Calculator className="w-3 h-3" />
              Estimator
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#2D3748] font-cabinet">
              {title}
            </h3>
          </div>
          {result && (
            <div className="flex items-center gap-2">
              {mode === "premium" && (
                <EmailEstimateButton
                  email={email}
                  payload={payload}
                />
              )}
              <PrintPdfButton
                result={result}
                perBank={banksSorted}
                inputs={inputs}
              />
            </div>
          )}
        </div>
      </div>

      {/* Inputs */}
      <div className="p-5 sm:p-6 bg-gradient-to-b from-[#F8FDFD] to-white border-b border-gray-100">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Price basis */}
          <div className="sm:col-span-2 lg:col-span-3">
            <Label>Price basis</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <ChoicePill
                active={inputs.priceBasis === "home"}
                onClick={() => setI({ priceBasis: "home" })}
                testid="basis-home"
              >
                Home I'm looking at
              </ChoicePill>
              <ChoicePill
                active={inputs.priceBasis === "qualified"}
                onClick={() => canUseQualified && setI({ priceBasis: "qualified" })}
                disabled={!canUseQualified}
                testid="basis-qualified"
              >
                What I qualify for
              </ChoicePill>
            </div>
            {!canUseQualified && (
              <p className="text-[11px] text-gray-400 mt-1">
                "What I qualify for" needs a qualifying loan amount.
              </p>
            )}
          </div>

          {/* Price */}
          <div className="sm:col-span-1">
            <Label>Home price (BSD)</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="number"
                min="0"
                step="1000"
                value={inputs.price}
                onChange={(e) => setI({ price: Number(e.target.value), priceBasis: "home" })}
                className="pl-10 h-11"
                data-testid="estimator-price-input"
              />
            </div>
          </div>

          {/* Down % */}
          <div className="sm:col-span-1">
            <Label>Down payment ({pct(inputs.down_pct)})</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={Math.round(inputs.down_pct * 100)}
                onChange={(e) => setI({ down_pct: Number(e.target.value) / 100 })}
                className="w-full accent-[#52BFB8]"
                data-testid="estimator-downpct-range"
              />
              <Input
                type="number"
                min="0"
                max="100"
                value={Math.round(inputs.down_pct * 100)}
                onChange={(e) => setI({ down_pct: Math.max(0, Math.min(100, Number(e.target.value))) / 100 })}
                className="w-16 h-11 text-center"
                data-testid="estimator-downpct-input"
              />
            </div>
          </div>

          {/* Buyer status */}
          <div>
            <Label>Buyer status</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <ChoicePill
                active={inputs.is_bahamian}
                onClick={() => setI({ is_bahamian: true })}
                testid="status-bahamian"
              >
                Bahamian
              </ChoicePill>
              <ChoicePill
                active={!inputs.is_bahamian}
                onClick={() => setI({ is_bahamian: false, first_time: false })}
                testid="status-non-bahamian"
              >
                Non-Bahamian
              </ChoicePill>
            </div>
          </div>

          {/* First-time (only shown if Bahamian) */}
          {inputs.is_bahamian && (
            <div>
              <Label>First-time home buyer?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <ChoicePill
                  active={inputs.first_time}
                  onClick={() => setI({ first_time: true })}
                  testid="firsttime-yes"
                >
                  Yes
                </ChoicePill>
                <ChoicePill
                  active={!inputs.first_time}
                  onClick={() => setI({ first_time: false })}
                  testid="firsttime-no"
                >
                  No
                </ChoicePill>
              </div>
            </div>
          )}

          {/* Property type */}
          <div>
            <Label>Property type</Label>
            <select
              value={inputs.property_type}
              onChange={(e) => setI({ property_type: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm text-[#2D3748] focus:outline-none focus:border-[#52BFB8] focus:ring-2 focus:ring-[#52BFB8]/20"
              data-testid="estimator-property-type"
            >
              <option value="single_family">Single-family</option>
              <option value="condo">Condo</option>
              <option value="multi_unit">Duplex–Fourplex (owner-occupied)</option>
            </select>
          </div>

          {/* Age band */}
          <div>
            <Label>Age band</Label>
            <select
              value={inputs.age_band}
              onChange={(e) => setI({ age_band: e.target.value })}
              className="mt-1 w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm text-[#2D3748] focus:outline-none focus:border-[#52BFB8] focus:ring-2 focus:ring-[#52BFB8]/20"
              data-testid="estimator-age-band"
            >
              <option value="under_40">Under 40</option>
              <option value="40_50">40–50</option>
              <option value="over_50">Over 50</option>
            </select>
          </div>

          {/* Coastal */}
          <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between rounded-xl bg-white border border-gray-100 px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-500" />
              <div>
                <div className="text-sm font-medium text-[#2D3748]">Coastal / higher-risk property</div>
                <div className="text-[11px] text-gray-500">Home insurance jumps to 1.25% if yes</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={!!inputs.coastal}
              onChange={(e) => setI({ coastal: e.target.checked })}
              className="w-5 h-5 accent-[#52BFB8]"
              data-testid="estimator-coastal"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="p-5 sm:p-6">
        {error && (
          <div className="mb-4 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700" data-testid="estimator-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading && !result && (
          <div className="py-10 text-center text-gray-400 text-sm">Calculating…</div>
        )}

        {sections && (
          <>
            {/* Hero total */}
            <div
              className="rounded-2xl bg-gradient-to-br from-[#52BFB8]/10 to-emerald-50 border border-[#52BFB8]/20 p-5 sm:p-6 mb-5 text-center"
              data-testid="estimator-total"
            >
              <div className="text-xs uppercase tracking-widest text-[#3E9690] font-semibold mb-1">
                Total cash needed at closing
              </div>
              <div className="text-4xl sm:text-5xl font-bold text-[#2D3748] font-cabinet">
                {money(result.total_out_of_pocket)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Down payment {money(result.down_payment)} + closing costs {money(result.closing_costs)}
                {" "}· <span className="text-[#3E9690] font-semibold">{pct(result.closing_pct)} of price</span>
              </div>
              {sections.government_taxes.exemption_applied && (
                <div
                  className="inline-flex items-center gap-1.5 mt-3 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1"
                  data-testid="estimator-exemption-chip"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  First-time buyer exemption applied — saves {money(sections.government_taxes.exemption_savings)}
                </div>
              )}
            </div>

            {/* Sections */}
            <Section
              title="Down Payment"
              icon={<Home className="w-4 h-4" />}
              note="Your initial cash into the home. Loan = price − down payment."
            >
              <Line label="Purchase price" value={money(sections.down_payment.price)} meta="input" />
              <Line
                label={`Down payment (${pct(sections.down_payment.down_pct)})`}
                value={money(sections.down_payment.down_payment)}
              />
              <Line label="Loan amount" value={money(sections.down_payment.loan)} meta="price − down" />
            </Section>

            <Section
              title="Government Taxes"
              icon={<FileText className="w-4 h-4" />}
              note="Buyer pays half of the 10% transfer VAT. Mortgage VAT is 1% of the loan. First-time Bahamian buyers get the first $299,000 exempt."
            >
              <Line
                label={`Transfer VAT – buyer's half (${pct(sections.government_taxes.transfer_vat_rate)}) on ${money(sections.government_taxes.taxable_amount)}`}
                value={money(sections.government_taxes.transfer_vat_buyer)}
                testid="line-transfer-vat"
              />
              {sections.government_taxes.exemption_applied && (
                <Line
                  label={`First-time buyer exemption on first ${money(sections.government_taxes.exemption_cap)}`}
                  value={`−${money(sections.government_taxes.exemption_savings)}`}
                  tone="savings"
                  testid="line-exemption"
                />
              )}
              <Line
                label={`Mortgage VAT (${pct(sections.government_taxes.mortgage_vat_rate)} of loan)`}
                value={sections.government_taxes.mortgage_vat_waived ? "WAIVED" : money(sections.government_taxes.mortgage_vat)}
                testid="line-mortgage-vat"
              />
            </Section>

            <Section
              title="Legal & Bank Fees"
              icon={<FileText className="w-4 h-4" />}
              note="Attorney fee is 2.5% of price plus 10% VAT. Bank commitment fee is usually 2% of the loan but varies by lender."
            >
              <Line
                label={`Attorney fee (${pct(sections.legal_bank_fees.legal_fee_rate)} of price)`}
                value={money(sections.legal_bank_fees.legal_fee)}
              />
              <Line
                label={`VAT on attorney fee (${pct(sections.legal_bank_fees.legal_fee_vat_rate)})`}
                value={money(sections.legal_bank_fees.legal_fee_vat)}
              />
              <Line
                label={`Bank / commitment fee (${pct(sections.legal_bank_fees.bank_fee_rate)} of loan)`}
                value={money(sections.legal_bank_fees.bank_fee)}
                testid="line-bank-fee"
              />
              <Line label="Appraisal" value={money(sections.legal_bank_fees.appraisal)} meta="flat" />
            </Section>

            <Section
              title="Insurance (first year, due at closing)"
              icon={<Shield className="w-4 h-4" />}
              note="Homeowners year-one premium and mortgage life insurance by age band. Over-50 should get a direct quote."
            >
              <Line
                label={`Homeowners insurance (${pct(sections.insurance.home_insurance_rate)} of price${sections.insurance.coastal ? ", coastal rate" : ""})`}
                value={money(sections.insurance.home_insurance)}
              />
              <Line
                label={`Mortgage life insurance (${pct(sections.insurance.life_insurance_rate)} of loan${sections.insurance.life_needs_quote ? " — get a quote" : ""})`}
                value={money(sections.insurance.life_insurance)}
              />
            </Section>

            {/* Subtotal */}
            <div className="mt-4 border-t pt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Closing costs <span className="text-gray-400">({pct(result.closing_pct)} of price)</span>
              </div>
              <div className="text-lg font-bold text-[#2D3748] font-cabinet" data-testid="estimator-closing-subtotal">
                {money(result.closing_costs)}
              </div>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <div className="text-sm font-semibold text-[#2D3748]">TOTAL OUT OF POCKET</div>
              <div className="text-2xl font-bold text-[#3E9690] font-cabinet" data-testid="estimator-grand-total">
                {money(result.total_out_of_pocket)}
              </div>
            </div>

            {/* Scenario compare — save this estimate as A/B and see the delta */}
            <ScenarioCompare
              inputs={inputs}
              result={result}
              onLoadScenario={(saved) => setInputs((p) => ({ ...p, ...saved.inputs }))}
            />

            {/* Per-bank comparison (only in pre-qual mode) */}
            {banksSorted.length > 0 && (
              <div className="mt-6 pt-5 border-t border-gray-100" data-testid="estimator-bank-compare">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-[#2D3748]">Per-bank cash to close</h4>
                  <div className="flex items-center gap-1 text-[11px]">
                    <span className="text-gray-400">Sort:</span>
                    <button
                      type="button"
                      onClick={() => setSortBy("lowest_cash")}
                      className={`px-2 py-0.5 rounded ${sortBy === "lowest_cash" ? "bg-[#52BFB8]/10 text-[#3E9690] font-semibold" : "text-gray-500"}`}
                      data-testid="sort-lowest-cash"
                    >
                      Lowest cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setSortBy("bank_name")}
                      className={`px-2 py-0.5 rounded ${sortBy === "bank_name" ? "bg-[#52BFB8]/10 text-[#3E9690] font-semibold" : "text-gray-500"}`}
                      data-testid="sort-bank-name"
                    >
                      A–Z
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {banksSorted.map((row, i) => (
                    <BankRow key={row.bank_id || row.bank_name || i} row={row} rank={i + 1} />
                  ))}
                </div>
              </div>
            )}

            <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
              {result.disclaimer}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================ Small building blocks ============================ */

function Label({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
      {children}
    </div>
  );
}

function ChoicePill({ active, onClick, disabled, testid, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      className={`h-10 rounded-lg text-sm font-medium transition ${
        disabled
          ? "bg-gray-50 text-gray-300 border border-gray-100 cursor-not-allowed"
          : active
          ? "bg-[#52BFB8] text-white border border-[#52BFB8] shadow-sm"
          : "bg-white text-[#2D3748] border border-gray-200 hover:border-[#52BFB8]/50"
      }`}
    >
      {children}
    </button>
  );
}

function Section({ title, icon, note, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 first:mt-0 bg-white rounded-xl border border-gray-100">
      <div className="flex items-center gap-2 px-4 pt-4">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[#52BFB8]/10 text-[#3E9690]">
          {icon}
        </span>
        <h4 className="text-sm font-bold text-[#2D3748] uppercase tracking-wider">{title}</h4>
      </div>
      <div className="px-4 pb-3">{children}</div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full border-t border-gray-100 py-2 px-4 flex items-center justify-between text-[11px] text-gray-500 hover:text-[#3E9690] transition"
      >
        <span className="inline-flex items-center gap-1">
          <Info className="w-3 h-3" />
          How this was calculated
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 text-[11px] text-gray-500 leading-relaxed">{note}</div>
      )}
    </div>
  );
}

function Line({ label, value, meta, tone, testid }) {
  const toneCls = tone === "savings" ? "text-emerald-600 font-semibold" : "text-[#2D3748]";
  return (
    <div className="py-1.5 flex items-start justify-between gap-3 border-b last:border-b-0 border-gray-50" data-testid={testid}>
      <div className="text-sm text-gray-600 leading-snug">
        {label}
        {meta && <span className="block text-[10px] text-gray-400">{meta}</span>}
      </div>
      <div className={`text-sm font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

function BankRow({ row, rank }) {
  const [open, setOpen] = useState(false);
  const b = row.breakdown;
  return (
    <div className="rounded-xl border border-gray-100 bg-white" data-testid={`bank-row-${row.bank_id || row.bank_name}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2.5 flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-bold ${
            rank === 1 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
          }`}>
            {rank}
          </span>
          <span className="text-sm font-semibold text-[#2D3748] text-left">
            {row.bank_name || row.bank_id}
          </span>
          {Object.keys(row.overrides_used || {}).length === 0 && (
            <span className="text-[10px] bg-gray-50 text-gray-400 px-1.5 py-0.5 rounded">default rates</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-xs text-gray-400 leading-none">Cash to close</div>
            <div className="text-sm font-bold text-[#2D3748] font-cabinet">{money(row.total_out_of_pocket)}</div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && b && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-50">
          <div className="text-[11px] text-gray-500 space-y-1">
            <div className="flex justify-between">
              <span>Bank fee ({pct(b.sections.legal_bank_fees.bank_fee_rate)} of loan)</span>
              <span className="font-semibold">{money(b.sections.legal_bank_fees.bank_fee)}</span>
            </div>
            <div className="flex justify-between">
              <span>Home insurance ({pct(b.sections.insurance.home_insurance_rate)} of price)</span>
              <span className="font-semibold">{money(b.sections.insurance.home_insurance)}</span>
            </div>
            <div className="flex justify-between">
              <span>Mortgage life ({pct(b.sections.insurance.life_insurance_rate)} of loan)</span>
              <span className="font-semibold">{money(b.sections.insurance.life_insurance)}</span>
            </div>
            <div className="flex justify-between pt-1 border-t border-gray-50">
              <span>Closing costs</span>
              <span className="font-semibold">{money(b.closing_costs)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MockBreakdown() {
  return (
    <div className="space-y-2 text-left">
      <div className="h-10 rounded bg-white/10" />
      <div className="h-6 rounded bg-white/5" />
      <div className="h-6 rounded bg-white/5" />
      <div className="h-6 rounded bg-white/5" />
      <div className="h-10 rounded bg-white/10 mt-6" />
    </div>
  );
}

/* ================ Scenario Compare ================= */

function ScenarioCompare({ inputs, result, onLoadScenario }) {
  // Two slots: A and B. Each stores inputs + total. Persisted in localStorage
  // so the comparison survives a page reload / payment round-trip.
  const KEY = "financify.estimator.scenarios";
  const [scenarios, setScenarios] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(KEY) : null;
      return raw ? JSON.parse(raw) : { A: null, B: null };
    } catch {
      return { A: null, B: null };
    }
  });

  const persist = (next) => {
    setScenarios(next);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      /* ignore quota */
    }
  };

  const save = (slot) => {
    const snapshot = {
      inputs: { ...inputs },
      summary: {
        price: result.sections.down_payment.price,
        down_payment: result.down_payment,
        down_pct: result.sections.down_payment.down_pct,
        loan: result.loan,
        closing_costs: result.closing_costs,
        total_out_of_pocket: result.total_out_of_pocket,
      },
      saved_at: Date.now(),
    };
    persist({ ...scenarios, [slot]: snapshot });
    toast.success(`Saved as Scenario ${slot}`);
  };

  const clear = (slot) => {
    persist({ ...scenarios, [slot]: null });
  };

  const bothSet = scenarios.A && scenarios.B;
  const delta = bothSet
    ? scenarios.B.summary.total_out_of_pocket - scenarios.A.summary.total_out_of_pocket
    : 0;

  return (
    <div className="mt-6 pt-5 border-t border-gray-100" data-testid="scenario-compare">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h4 className="text-sm font-bold text-[#2D3748]">Compare scenarios</h4>
          <p className="text-[11px] text-gray-500">
            Save this estimate as A or B to see the real cash-to-close delta side-by-side.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => save("A")}
            className="h-8"
            data-testid="scenario-save-a"
          >
            Save as A
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => save("B")}
            className="h-8"
            data-testid="scenario-save-b"
          >
            Save as B
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ScenarioCard
          slot="A"
          scenario={scenarios.A}
          onLoad={() => scenarios.A && onLoadScenario(scenarios.A)}
          onClear={() => clear("A")}
          tone="teal"
        />
        <ScenarioCard
          slot="B"
          scenario={scenarios.B}
          onLoad={() => scenarios.B && onLoadScenario(scenarios.B)}
          onClear={() => clear("B")}
          tone="emerald"
        />
      </div>

      {bothSet && (
        <div
          className={`mt-3 rounded-xl p-4 text-center ${
            delta === 0
              ? "bg-gray-50 border border-gray-100"
              : delta > 0
              ? "bg-rose-50 border border-rose-100"
              : "bg-emerald-50 border border-emerald-100"
          }`}
          data-testid="scenario-delta"
        >
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
            B minus A · cash to close
          </div>
          <div
            className={`text-2xl font-bold font-cabinet mt-1 ${
              delta === 0 ? "text-gray-500" : delta > 0 ? "text-rose-600" : "text-emerald-600"
            }`}
          >
            {delta > 0 ? "+" : delta < 0 ? "−" : ""}
            {money(Math.abs(delta))}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {delta > 0
              ? "Scenario B needs more cash at closing than Scenario A."
              : delta < 0
              ? "Scenario B saves you cash at closing vs Scenario A."
              : "Both scenarios need the exact same cash at closing."}
          </div>
        </div>
      )}
    </div>
  );
}

function ScenarioCard({ slot, scenario, onLoad, onClear, tone }) {
  const toneBg = tone === "teal" ? "from-[#52BFB8]/10 to-white" : "from-emerald-50 to-white";
  const toneBorder = tone === "teal" ? "border-[#52BFB8]/30" : "border-emerald-200";
  if (!scenario) {
    return (
      <div
        className={`rounded-xl border-2 border-dashed ${toneBorder} bg-gradient-to-b ${toneBg} p-4 text-center`}
        data-testid={`scenario-${slot}-empty`}
      >
        <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
          Scenario {slot}
        </div>
        <div className="text-sm text-gray-400 mt-2">
          Tap <span className="font-semibold text-[#2D3748]">Save as {slot}</span> to lock in your current numbers.
        </div>
      </div>
    );
  }
  const s = scenario.summary;
  const i = scenario.inputs;
  return (
    <div
      className={`rounded-xl border ${toneBorder} bg-gradient-to-b ${toneBg} p-4`}
      data-testid={`scenario-${slot}-filled`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
          Scenario {slot}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onLoad}
            className="text-[10px] text-[#3E9690] font-semibold uppercase hover:underline"
            data-testid={`scenario-${slot}-load`}
          >
            Load
          </button>
          <span className="text-gray-200">·</span>
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] text-gray-400 font-semibold uppercase hover:text-rose-500"
            data-testid={`scenario-${slot}-clear`}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="text-xs text-gray-500 leading-snug">
        {money(s.price)} price · {pct(s.down_pct)} down ·{" "}
        {i.is_bahamian ? (i.first_time ? "Bahamian · First-time" : "Bahamian") : "Non-Bahamian"}
        {i.coastal ? " · coastal" : ""}
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <span className="text-[11px] text-gray-500">Total cash</span>
        <span className="text-xl font-bold text-[#2D3748] font-cabinet">
          {money(s.total_out_of_pocket)}
        </span>
      </div>
      <div className="flex items-baseline justify-between text-[11px] text-gray-500">
        <span>Down · Closing</span>
        <span className="font-mono">
          {money(s.down_payment)} + {money(s.closing_costs)}
        </span>
      </div>
    </div>
  );
}

/* =============== Email + PDF actions ============== */

function EmailEstimateButton({ email, payload }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const onClick = async () => {
    if (!email) {
      toast.error("No email on file for this estimate.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/estimator/email`, { ...payload, email });
      setSent(true);
      toast.success(`Estimate sent to ${email}`);
      setTimeout(() => setSent(false), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send email.");
    } finally {
      setSending(false);
    }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={sending || sent}
      className="h-9 inline-flex items-center gap-1.5"
      data-testid="estimator-email-btn"
    >
      {sending ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : sent ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
      ) : (
        <Mail className="w-3.5 h-3.5" />
      )}
      {sent ? "Sent" : "Email me"}
    </Button>
  );
}

function PrintPdfButton({ result, perBank, inputs }) {
  const onClick = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const s = result.sections;
    const line = (label, val, strong = false) =>
      `<tr><td style="padding:4px 0;color:#4a5568;${strong ? "font-weight:700;color:#1a202c" : ""}">${label}</td><td style="padding:4px 0;text-align:right;${strong ? "font-weight:700;color:#1a202c" : ""}">${val}</td></tr>`;
    const bankTable = perBank && perBank.length
      ? `<h2 style="font-size:14px;margin:28px 0 8px;color:#2d3748;">Per-bank comparison</h2>
         <table style="width:100%;border-collapse:collapse;font-size:12px;">
         <tr style="background:#f8fafc;"><th style="text-align:left;padding:6px;">Bank</th><th style="text-align:right;padding:6px;">Bank fee %</th><th style="text-align:right;padding:6px;">Home ins %</th><th style="text-align:right;padding:6px;">Cash to close</th></tr>
         ${perBank.map(r => `<tr style="border-bottom:1px solid #edf2f7;"><td style="padding:6px;">${r.bank_name || r.bank_id}</td><td style="padding:6px;text-align:right;">${pct(r.breakdown.sections.legal_bank_fees.bank_fee_rate)}</td><td style="padding:6px;text-align:right;">${pct(r.breakdown.sections.insurance.home_insurance_rate)}</td><td style="padding:6px;text-align:right;font-weight:700;">${money(r.total_out_of_pocket)}</td></tr>`).join("")}
         </table>`
      : "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Financify Closing Cost Estimate</title>
      <style>
        body { font-family: Arial, sans-serif; color:#2d3748; max-width:720px; margin:40px auto; padding:0 24px; }
        h1 { font-size:22px; margin:0 0 4px; }
        .meta { color:#718096; font-size:12px; margin-bottom:24px; }
        .section { margin:18px 0; padding:12px; background:#f8fafc; border-radius:8px; }
        .section h2 { font-size:13px; margin:0 0 8px; text-transform:uppercase; letter-spacing:1px; color:#4a5568; }
        table { width:100%; border-collapse:collapse; font-size:13px; }
        .total { font-size:18px; color:#3e9690; font-weight:700; }
        .disclaimer { font-size:11px; color:#a0aec0; margin-top:30px; line-height:1.5; }
        @media print { body { margin:0; padding:16px; } }
      </style></head><body>
        <h1>Closing Cost & Out-of-Pocket Estimate</h1>
        <div class="meta">Financify — ${new Date().toLocaleDateString()} · BSD</div>
        <div class="section">
          <h2>Inputs</h2>
          <table>
            ${line("Purchase price", money(s.down_payment.price))}
            ${line("Down payment %", pct(s.down_payment.down_pct))}
            ${line("Buyer status", inputs.is_bahamian ? (inputs.first_time ? "Bahamian · First-time buyer" : "Bahamian") : "Non-Bahamian")}
            ${line("Property type", (inputs.property_type || "").replace("_", " "))}
            ${line("Age band", (inputs.age_band || "").replace("_", "–"))}
            ${line("Coastal", inputs.coastal ? "Yes" : "No")}
          </table>
        </div>
        <div class="section">
          <h2>Down Payment</h2>
          <table>
            ${line("Down payment", money(s.down_payment.down_payment))}
            ${line("Loan amount", money(s.down_payment.loan))}
          </table>
        </div>
        <div class="section">
          <h2>Government Taxes</h2>
          <table>
            ${line(`Transfer VAT (${pct(s.government_taxes.transfer_vat_rate)}) on ${money(s.government_taxes.taxable_amount)}`, money(s.government_taxes.transfer_vat_buyer))}
            ${s.government_taxes.exemption_applied ? line(`First-time exemption (first ${money(s.government_taxes.exemption_cap)})`, `−${money(s.government_taxes.exemption_savings)}`) : ""}
            ${line(`Mortgage VAT (${pct(s.government_taxes.mortgage_vat_rate)} of loan)`, s.government_taxes.mortgage_vat_waived ? "WAIVED" : money(s.government_taxes.mortgage_vat))}
          </table>
        </div>
        <div class="section">
          <h2>Legal & Bank Fees</h2>
          <table>
            ${line(`Attorney fee (${pct(s.legal_bank_fees.legal_fee_rate)})`, money(s.legal_bank_fees.legal_fee))}
            ${line(`VAT on attorney fee (${pct(s.legal_bank_fees.legal_fee_vat_rate)})`, money(s.legal_bank_fees.legal_fee_vat))}
            ${line(`Bank fee (${pct(s.legal_bank_fees.bank_fee_rate)} of loan)`, money(s.legal_bank_fees.bank_fee))}
            ${line("Appraisal", money(s.legal_bank_fees.appraisal))}
          </table>
        </div>
        <div class="section">
          <h2>Insurance</h2>
          <table>
            ${line(`Homeowners (${pct(s.insurance.home_insurance_rate)})`, money(s.insurance.home_insurance))}
            ${line(`Mortgage life (${pct(s.insurance.life_insurance_rate)})`, money(s.insurance.life_insurance))}
          </table>
        </div>
        <div class="section" style="background:#f0faf9;">
          <table>
            ${line(`Closing costs (${pct(result.closing_pct)} of price)`, money(result.closing_costs), true)}
            <tr><td style="padding:10px 0;font-size:16px;font-weight:700;">TOTAL OUT OF POCKET</td><td style="padding:10px 0;text-align:right;" class="total">${money(result.total_out_of_pocket)}</td></tr>
          </table>
        </div>
        ${bankTable}
        <p class="disclaimer">${result.disclaimer}</p>
        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`;
    w.document.write(html);
    w.document.close();
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className="h-9 inline-flex items-center gap-1.5"
      data-testid="estimator-download-pdf"
    >
      <Download className="w-3.5 h-3.5" />
      PDF
    </Button>
  );
}
